import React from 'react';
import { apiService } from '../../services/api';
import * as fmt from '../../lib/format';
import { StatusPill } from './PlanTable';
import { FunctionKey, FunctionsData } from './types';
import { usePlanView } from './usePlanView';
import ViewState from './ViewState';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';

// Which of the five functions is on each goal, read from who the tickets are assigned to (and, for a ticket with
// nobody on it, which function does that type of ticket). One sentence per function, then goal by function.
export default function FunctionMatrix({ quarter, projectKey, onOpen }: { quarter: string | null; projectKey: string | null; onOpen: (key: string) => void }) {
  const { data, error, loading, reload } = usePlanView<FunctionsData>(() => apiService.getPlanFunctions(quarter), `${quarter ?? ''}`);
  const goals = (data?.goals ?? []).filter((g) => !projectKey || g.project_key === projectKey);
  const columns: FunctionKey[] = (data?.functions ?? []).map((f) => f.key);
  return (
    <ViewState loading={loading && !data} error={error} onRetry={reload} empty={data && data.goal_count === 0 ? 'No goals in this quarter yet.' : null}>
      {data && (
        <>
          <ul className={`${CARD} mb-5 divide-y divide-gray-100`} aria-label="Each function">
            {data.functions.map((f) => (
              <li key={f.key} className="px-5 py-2.5 text-sm text-gray-800">{f.sentence}</li>
            ))}
          </ul>
          <section className={`${CARD} overflow-hidden`} aria-label="Goals by function">
            <div className="relative overflow-x-auto">
              <table className="w-full border-separate border-spacing-0 text-left text-sm">
                <caption className="sr-only">Open and done tickets for each goal, by function</caption>
                <thead>
                  <tr className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <th scope="col" className="sticky left-0 z-10 min-w-[12rem] border-b border-gray-200 bg-white px-4 py-2 sm:min-w-[18rem]">Goal</th>
                    {columns.map((k) => (
                      <th key={k} scope="col" className="min-w-[6rem] border-b border-gray-200 px-3 py-2">{k === 'Unassigned' ? 'Nobody yet' : k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {goals.map((g) => (
                    <tr key={g.key} className="hover:bg-gray-50">
                      <th scope="row" className="sticky left-0 z-10 border-b border-gray-100 bg-white px-4 py-2.5 text-left align-top font-normal">
                        <button type="button" onClick={() => onOpen(g.key)} className="block max-w-[18rem] break-words rounded text-left leading-snug text-gray-900 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                          {g.title}
                        </button>
                        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.6875rem] text-gray-600">
                          <span>{g.project_name ?? g.project_key}</span>
                          <StatusPill row={{ plan_status: g.plan_status, jira_status: null, sheet_status: null }} />
                        </span>
                      </th>
                      {columns.map((k) => {
                        const c = g.cells[k];
                        return (
                          <td key={k} className="border-b border-gray-100 px-3 py-2.5 align-top">
                            {c ? (
                              <span aria-label={`${fmt.count(c.open)} open, ${fmt.count(c.done)} done`}>
                                <span className="font-medium tabular-nums text-gray-900">{fmt.count(c.open)} open</span>
                                <span className="block text-xs tabular-nums text-gray-600">{fmt.count(c.done)} done</span>
                              </span>
                            ) : (
                              <span aria-hidden className="text-gray-400">–</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </ViewState>
  );
}
